/**
 * 3.0 콜랩 상세 스타일 — 커버 · 기본 정보 · 주최자의 말.
 * @description 값은 `--fw-*` 토큰만 읽는다. 앱 3.0 상세의 순서(제목 그룹 → 언제·어디 → 주최 → 주최자의
 *   말)를 2.0 웹 상세의 껍데기(폰 한 열 · 데스크톱 두 열)에 얹는다.
 *
 *   커버는 앱처럼 폰에서 1:1 로 화면 폭을 꽉 채운다. 넓은 화면에서는 3:2 상자 안에 포스터 전체를
 *   담고(세로 포스터가 대부분이라 잘라 내면 글자가 날아간다) 같은 그림을 흐리게 깔아 빈 곳을 채운다.
 */
import { cva } from "class-variance-authority";

export const collabV3BadgeStyles = cva(
  [
    "fw-caption-strong inline-flex w-fit shrink-0 items-center gap-[var(--fw-space-gap-xs)]",
    "rounded-[var(--fw-radius-full)] px-[var(--fw-space-inset-sm)] py-[var(--fw-space-inset-xxs)]",
  ],
  {
    variants: {
      tone: {
        neutral:
          "bg-[var(--fw-color-surface-neutral-tertiary)] text-[var(--fw-color-content-neutral-secondary)]",
        inverse:
          "bg-[var(--fw-color-surface-neutral-inverse)] text-[var(--fw-color-content-neutral-inverse)]",
        highlight:
          "bg-[var(--fw-color-surface-highlight-secondary)] text-[var(--fw-color-content-highlight-primary)]",
        success:
          "bg-[var(--fw-color-surface-success-secondary)] text-[var(--fw-color-content-success-primary)]",
        brand:
          "bg-[var(--fw-color-surface-brand-secondary)] text-[var(--fw-color-content-brand-primary)]",
      },
    },
  },
);

export const collabV3DetailStyles = {
  liveDot:
    "size-[var(--fw-space-inset-sm)] shrink-0 animate-pulse rounded-[var(--fw-radius-full)] bg-[var(--fw-color-content-critical-primary)]",
  cover: {
    root: [
      "relative -mx-[var(--fw-inset-screen)] aspect-square w-[calc(100%+var(--fw-inset-screen)*2)] shrink-0 overflow-hidden",
      "bg-[var(--fw-color-surface-neutral-secondary)]",
      "tablet:mx-0 tablet:aspect-[3/2] tablet:w-full tablet:rounded-[var(--fw-radius-xl)]",
    ].join(" "),
    /** 넓은 화면에서만 — 포스터 뒤를 같은 그림으로 흐리게 채운다 */
    backdrop: "absolute inset-0 hidden scale-110 opacity-60 blur-2xl tablet:block",
    /** 폰은 꽉 채우고(1:1), 넓은 화면은 포스터 전체를 담는다 */
    photo: "absolute inset-0 tablet:[&_img]:object-contain",
    badge: "absolute left-[var(--fw-space-inset-lg)] top-[var(--fw-space-inset-lg)]",
  },
  info: {
    root: "flex w-full flex-col gap-[var(--fw-space-gap-md)]",
    badges: "flex flex-wrap items-center gap-[var(--fw-space-gap-xs)]",
    title:
      "fw-h2 break-words text-[var(--fw-color-content-neutral-primary)] desktop:fw-h1",
    cancelNotice: "fw-b2 text-[var(--fw-color-content-critical-primary)]",
  },
  participants: {
    root: "flex w-fit cursor-pointer items-center gap-[var(--fw-space-gap-sm)] text-left",
    stack: "flex shrink-0 items-center [&>*+*]:-ml-[var(--fw-space-inset-sm)]",
    stackItem:
      "flex rounded-[var(--fw-radius-full)] ring-[length:var(--fw-border-lg)] ring-[var(--fw-color-surface-neutral-primary)]",
    text: "fw-b2 min-w-0 text-[var(--fw-color-content-neutral-tertiary)]",
    names: "fw-l2 text-[var(--fw-color-content-neutral-secondary)]",
  },
  row: {
    list: "flex w-full flex-col",
    root: "flex w-full items-center gap-[var(--fw-space-gap-md)] py-[var(--fw-space-inset-sm)]",
    link: "rounded-[var(--fw-radius-md)] transition-colors hover:bg-[var(--fw-color-surface-neutral-transparent-hovered)]",
    icon: "size-[var(--fw-size-icon-sm)] shrink-0 text-[var(--fw-color-content-neutral-secondary)]",
    texts: "flex min-w-0 flex-1 flex-col",
    title: "fw-l1 break-words text-[var(--fw-color-content-neutral-primary)]",
    meta: "fw-caption text-[var(--fw-color-content-neutral-tertiary)]",
    chevron:
      "size-[var(--fw-size-icon-xs)] shrink-0 text-[var(--fw-color-content-neutral-tertiary)]",
  },
  host: {
    root: "flex items-center gap-[var(--fw-space-gap-md)]",
    text: "fw-b2 min-w-0 truncate text-[var(--fw-color-content-neutral-tertiary)]",
    name: "fw-l2 text-[var(--fw-color-content-neutral-primary)]",
  },
  note: {
    root: "flex w-full flex-col gap-[var(--fw-space-gap-md)]",
    text: "fw-b1 whitespace-pre-line break-words text-[var(--fw-color-content-neutral-primary)]",
    more: "fw-caption w-fit cursor-pointer text-[var(--fw-color-content-brand-primary)]",
    images: "grid grid-cols-2 gap-[var(--fw-space-gap-sm)] tablet:grid-cols-3",
    image:
      "relative aspect-square overflow-hidden rounded-[var(--fw-radius-md)] bg-[var(--fw-color-surface-neutral-secondary)]",
  },
};

/** 아바타 그림을 내려받을 폭 — DS `Avatar` 의 크기(24 · 32 · 48)와 같다 */
export const COLLAB_V3_AVATAR_WIDTH = { xs: 24, xssm: 32, sm: 48 } as const;

/** 커버 그림 폭 힌트 — 폰은 화면 폭, 넓은 화면은 열 폭 */
export const COLLAB_V3_COVER_SIZES =
  "(min-width: 1280px) 768px, (min-width: 768px) 760px, 100vw";
